import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useRouter } from '@/routing/navigation'
import { getReferralCookies } from '@/lib/utils/referral-cookie'
import Link from '@/routing/Link'
import { ArrowLeft, ArrowRight, Check, Loader2, X } from 'lucide-react'
import { authApi } from '@/lib/api'
import { useAuth } from '@/lib/config/auth-provider'
import { isPasswordValid } from '@/lib/utils/password-validation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Heading } from '@/components/ui/Heading'
import { Text } from '@/components/ui/Text'
import { PasswordRulesChecklist } from '@/components/auth/PasswordRulesChecklist'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { TosAgreeCopy } from '@/components/auth/TosAgreeCopy'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { safeReturnTo } from '@/lib/utils/safe-return-to'

export default function RegisterCompletePage() {
  const { t } = useTranslation(['auth', 'common'])
  const searchParams = useSearchParams()
  const router = useRouter()
  const { loginWithResponse } = useAuth()
  const [name, setName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [tosAccepted, setTosAccepted] = useState(false)

  const [accessCode, setAccessCode] = useState(searchParams.get('code') || '')
  const [codeValidated, setCodeValidated] = useState<boolean | null>(null)
  const [codeValidating, setCodeValidating] = useState(false)
  const [codeError, setCodeError] = useState('')

  const email = searchParams.get('email') || ''
  const inviteParam = searchParams.get('invite') || ''
  const { referralCode: referralCookieCode } = getReferralCookies()
  const isAccessCodeExempt = !!(inviteParam || referralCookieCode)

  useEffect(() => {
    if (!email) {
      router.replace('/register')
    }
  }, [email, router])

  useEffect(() => {
    const initialCode = searchParams.get('code') || ''
    if (!initialCode.trim()) return
    setCodeValidating(true)
    setCodeError('')
    authApi.validateAccessCode(initialCode.trim())
      .then(({ valid }) => {
        setCodeValidated(valid)
        if (!valid) setCodeError(t('complete.errors.invalidCode'))
      })
      .catch(() => {
        setCodeValidated(false)
        setCodeError(t('complete.errors.verifyFailed'))
      })
      .finally(() => setCodeValidating(false))
  }, [])

  if (!email) return null

  async function validateCode(code: string) {
    if (!code.trim()) {
      setCodeValidated(null)
      setCodeError('')
      return
    }
    setCodeValidating(true)
    setCodeError('')
    try {
      const { valid } = await authApi.validateAccessCode(code.trim())
      setCodeValidated(valid)
      if (!valid) setCodeError(t('complete.errors.invalidCode'))
    } catch {
      setCodeValidated(false)
      setCodeError(t('complete.errors.verifyFailed'))
    } finally {
      setCodeValidating(false)
    }
  }

  function handleCodeBlur() {
    if (accessCode.trim()) {
      validateCode(accessCode)
    } else {
      setCodeValidated(null)
      setCodeError('')
    }
  }

  function handleCodeChange(value: string) {
    setAccessCode(value)
    if (codeValidated !== null) {
      setCodeValidated(null)
      setCodeError('')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const { referralCode, referralSource } = getReferralCookies()
      const response = await authApi.register({
        email,
        password,
        name,
        promoCode: codeValidated === true ? accessCode.trim() : undefined,
        inviteToken: inviteParam || undefined,
        tosAcceptedAt: new Date().toISOString(),
        referralCode: referralCode || undefined,
        referralSource: referralSource || undefined,
      })

      const returnTo = searchParams.get('returnTo')

      if (response.pendingVerification) {
        const verifyParams = new URLSearchParams({ email })
        if (returnTo) verifyParams.set('returnTo', returnTo)
        router.push(`/register/verify?${verifyParams.toString()}`)
        return
      }

      loginWithResponse({ ...response, isNewUser: true })
      router.push(safeReturnTo(returnTo))
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, t('complete.errors.registrationFailed')))
    } finally {
      setLoading(false)
    }
  }

  const backParams = new URLSearchParams()
  if (email) backParams.set('email', email)
  if (inviteParam) backParams.set('invite', inviteParam)
  if (accessCode) backParams.set('code', accessCode)
  const backHref = `/register${backParams.toString() ? `?${backParams.toString()}` : ''}`

  const codeRequired = !isAccessCodeExempt
  const codeReady = isAccessCodeExempt || codeValidated === true
  const canSubmit = isPasswordValid(password) && name.trim().length > 0 && !loading && tosAccepted && codeReady && !codeValidating

  return (
    <div className="flex w-full flex-col gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col gap-1">
        <Heading level={1} size="3xl" className="text-center font-display font-normal tracking-tight">
          {t('complete.title')}
        </Heading>
        <Text as="p" size="sm" color="muted" className="text-center">
          {email}
        </Text>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">{t('complete.name')}</Label>
          <Input
            id="name"
            data-testid="register-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('complete.namePlaceholder')}
            required
            autoComplete="name"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">{t('password', { ns: 'common' })}</Label>
          <PasswordInput
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            required
            autoComplete="new-password"
          />
          <PasswordRulesChecklist password={password} />
        </div>

        {!isAccessCodeExempt && (
          <div className="space-y-2">
            <Label htmlFor="access-code">{codeRequired ? t('complete.accessCode') : t('complete.accessCodeOptional')}</Label>
            <div className="relative">
              <Input
                id="access-code"
                data-testid="register-access-code-input"
                type="text"
                value={accessCode}
                onChange={(e) => handleCodeChange(e.target.value)}
                onBlur={handleCodeBlur}
                placeholder={t('complete.accessCodePlaceholder')}
                autoComplete="off"
                className={codeValidated === false ? 'border-destructive pr-9' : codeValidated === true ? 'border-emerald-500 pr-9' : 'pr-9'}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {codeValidating && <Loader2 className="size-4 animate-spin text-muted-foreground" />}
                {!codeValidating && codeValidated === true && <Check className="size-4 text-emerald-500" />}
                {!codeValidating && codeValidated === false && <X className="size-4 text-destructive" />}
              </div>
            </div>
            {codeError && (
              <Text as="p" size="xs" data-testid="register-access-code-error" className="text-destructive">
                {codeError}
              </Text>
            )}
          </div>
        )}

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="tos"
            data-testid="register-tos-checkbox"
            checked={tosAccepted}
            onChange={(e) => setTosAccepted(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-primary"
          />
          <Label htmlFor="tos" className="text-sm font-normal leading-relaxed text-muted-foreground cursor-pointer">
            <TosAgreeCopy />
          </Label>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" variant="cta" disabled={!canSubmit} className="w-full gap-2">
          {loading ? (
            <>
              <Loader2 className="mr-2 size-4 animate-spin" />
              {t('complete.submitting')}
            </>
          ) : (
            <>
              {t('complete.submit')}
              <ArrowRight className="size-4" />
            </>
          )}
        </Button>
      </form>

      <Text as="p" size="sm" color="muted" className="text-center">
        <Button variant="link" size="sm" className="h-auto gap-1 p-0 text-sm" asChild>
          <Link href={backHref}>
            <ArrowLeft className="size-3" />
            {t('back', { ns: 'common' })}
          </Link>
        </Button>
      </Text>
    </div>
  )
}
